class PersonalizationSteps extends HTMLElement {
  constructor() {
    super();
    this.totalSteps = parseInt(this.dataset.totalSteps, 10) || 1;
    this.productFormId = this.dataset.productFormId;
    this._step = 1;
    this._maxReached = 1;

    this._panels = this.querySelectorAll('.ps-panel');
    this._stepPills = this.querySelectorAll('.ps-step');
    this._seps = this.querySelectorAll('.ps-step-sep');
    this._nextBtn = this.querySelector('[data-ps-action="next"]');
    this._prevBtn = this.querySelector('[data-ps-action="prev"]');
    this._nextLabel = this.querySelector('[data-ps-next-label]');
    this._nextIconArrow = this.querySelector('[data-ps-icon-next]');
    this._nextIconCart = this.querySelector('[data-ps-icon-cart]');
    this._atcText = this._nextBtn?.dataset.psAtcText || 'Add to cart';
    this._canAddToCart = this._nextBtn?.dataset.psCannotAdd !== 'true';

    // Translated button labels (populated from data attributes by Liquid's `| t`).
    // Fallbacks keep the component functional if a locale is missing keys.
    const ds = this.dataset;
    this._labelContinue = ds.labelContinue || 'Continue';
    this._labelReview = ds.labelReview || 'Review';
    this._labelMore = ds.labelMore || 'More';
    this._labelLess = ds.labelLess || 'Less';
    this._form = this.querySelector('form');
    this._variantSelects = this.querySelector('variant-selects');
    this._lastGrand = null;

    this.teleportBlocks();
    this.bindEvents();
    this.wireNameInputs();
    this.wireFontPicker();
    this.setupSelectInlineLabels();
    this.setupUpsells();
    this.setupJewelryPreview();
    this.setupPersonalizedUpsellGalleries();
    this.setupPersonalizedUpsellPreviewButtons();
    this.setupFormValidation();
    this.setupGlozinVariantOptions();
    this.setupPersonalizedUpsellDrawers();
    this.setupSuccessState();
    this.setupSuccessUpsells();
    this.updateStep(1, { scroll: false });
    this.populateSummary();
    this.recalcTotals({ animate: false });
  }

  /**
   * After a successful /cart/add triggered by THIS component's submit, swap
   * the whole shell to the "Added to cart" success state. Uses the theme's
   * pubsub `cartUpdate` event — it fires right after product-form.js's fetch
   * resolves. A one-shot flag ensures we only react to OUR own submission,
   * not to other forms/cart-drawer activity on the page.
   */
  setupSuccessState() {
    this._expectingCartUpdate = false;
    this._successEl = this.querySelector('[data-ps-success]');
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (!this._expectingCartUpdate) return;
      this._expectingCartUpdate = false;
      this.showSuccessState();
    });
  }

  showSuccessState() {
    this.setAttribute('data-ps-state', 'success');
    if (this._successEl) this._successEl.setAttribute('aria-hidden', 'false');
    // Scroll the block into view so the success message lands within the header offset.
    const headerOffset = this.getStickyHeaderOffset ? this.getStickyHeaderOffset() : 0;
    const top = this.getBoundingClientRect().top + window.scrollY - headerOffset - 12;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

    // Continue shopping → reload the page to reset all state cleanly.
    const resetBtn = this._successEl?.querySelector('[data-ps-action="reset"]');
    if (resetBtn && !resetBtn.dataset.psResetWired) {
      resetBtn.dataset.psResetWired = '1';
      resetBtn.addEventListener('click', () => window.location.reload());
    }
  }

  /**
   * Move every `[data-ps-teleport-to]` element into its target on mount.
   * Used by trust-badge / success-upsell blocks: they render in the upsell
   * stream (since `content_for 'blocks'` doesn't filter by type) but visually
   * belong somewhere else (e.g. inside `.ps-trust` / `.ps-success`).
   */
  teleportBlocks() {
    this.querySelectorAll('[data-ps-teleport-to]').forEach((el) => {
      const targetSel = el.dataset.psTeleportTo;
      if (!targetSel) return;
      const target = this.querySelector(targetSel);
      if (target && el.parentElement !== target) {
        target.appendChild(el);
      }
    });
  }

  /**
   * Copy the parent personalization-steps block's resolved `--ps-*` CSS
   * variables onto the given element as inline style overrides. Used before
   * reparenting drawers/overlays to <body> — once detached from the block,
   * the merchant's per-instance theme tokens (set via `<style>#ps-{id}{…}</style>`)
   * no longer apply, so the floating element falls back to the global defaults
   * unless we re-pin those values directly on it.
   */
  inheritBlockCustomProperties(target) {
    if (!target) return;
    const sourceStyle = getComputedStyle(this);
    // CSSStyleDeclaration doesn't enumerate custom properties; iterate the
    // declared index keys and pull any that look like a `--ps-*` token.
    const seen = new Set();
    for (let i = 0; i < sourceStyle.length; i++) {
      const name = sourceStyle[i];
      if (name && name.startsWith('--ps-') && !seen.has(name)) {
        seen.add(name);
        const value = sourceStyle.getPropertyValue(name);
        if (value) target.style.setProperty(name, value.trim());
      }
    }
  }

  /**
   * Update the server-rendered step-1 price markup with the current variant's
   * price + compare-at price. Initial render is server-side via the `price`
   * snippet; this only mutates text + on-sale / sold-out classes when the
   * shopper changes variants. We can't clone Shopify's `#price-{sectionId}`
   * because some product templates (this one included) don't render that
   * element — relying on it leaves the price stale on variant change.
   */
  syncStep1PriceMirror() {
    const mirror = this.querySelector('[data-ps-step1-price-mirror]');
    if (!mirror) return;
    const variant = this._currentVariant;
    if (!variant) return; // initial state: server-rendered, nothing to do

    const onSale = typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price;
    mirror.classList.toggle('ps-step1-price--on-sale', !!onSale);

    const current = mirror.querySelector('[data-ps-price-current]');
    if (current) current.textContent = this.formatMoney(variant.price);

    const compare = mirror.querySelector('[data-ps-price-compare]');
    if (compare) {
      if (onSale) {
        compare.textContent = this.formatMoney(variant.compare_at_price);
        compare.removeAttribute('hidden');
      } else {
        compare.setAttribute('hidden', '');
      }
    }
  }

  setupGlozinVariantOptions() {
    const variantsScript = this.querySelector('[data-ps-variants]');
    if (!variantsScript) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent || '[]');
    } catch (error) {
      return;
    }

    const groups = Array.from(this.querySelectorAll('[data-ps-option-position]'));
    if (!groups.length || !variants.length) return;

    const update = () => {
      const selectedOptions = groups.map((group) => {
        const selected = group.querySelector('input[type="radio"]:checked');
        const valueEl = group.querySelector('[data-selected-value]');
        if (selected && valueEl) valueEl.textContent = selected.value;
        return selected ? selected.value : null;
      });

      if (selectedOptions.some((option) => option === null)) return;

      const variant = variants.find((item) => {
        return item.options.every((option, index) => option === selectedOptions[index]);
      });
      if (!variant) return;

      this._currentVariant = variant;
      this.syncMainProductPrice(variant);

      const idInput = this._form?.querySelector('input[name="id"]');
      if (idInput) {
        const previousVariantId = idInput.value;
        idInput.value = variant.id;
        idInput.disabled = !variant.available;
        if (previousVariantId !== String(variant.id)) {
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
          this.updateVariantUrl(variant.id);
        }
      }

      this._canAddToCart = Boolean(variant.available);
      if (this._nextBtn) {
        this._nextBtn.toggleAttribute('disabled', this.step === this.totalSteps && !variant.available);
        if (variant.available) {
          delete this._nextBtn.dataset.psCannotAdd;
        } else {
          this._nextBtn.dataset.psCannotAdd = 'true';
        }
      }

      const labels = this.dataset;
      if (!variant.available) {
        this._atcText = labels.labelSoldOut || this._atcText;
      } else if (variant.inventory_management && variant.inventory_quantity < 1) {
        this._atcText = labels.labelPreOrder || this._atcText;
      } else {
        this._atcText = labels.labelAddToCart || this._atcText;
      }

      this.syncStep1PriceMirror();
      this.populateSummary();
      this.recalcTotals();
      this.updateNavButtons(this.step);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.variantChange, {
          data: { variant },
          source: 'personalization-steps',
          productVariantId: variant.id,
        });
      }
    };

    groups.forEach((group) => {
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.addEventListener('change', update);
      });
    });

    update();
  }

  syncMainProductPrice(variant) {
    const productTarget = this.closest('.product__item-js');
    if (!productTarget || !variant) return;

    const priceWrapper = productTarget.querySelector('.product-detail__price');
    if (priceWrapper) priceWrapper.classList.remove('visibility-hidden', 'hidden');

    const priceFormat = this.formatMoney(variant.price);
    const priceEl = priceWrapper?.querySelector('.price-regular .price');
    if (priceEl) priceEl.innerHTML = priceFormat;

    if (variant.unit_price && variant.unit_price_measurement) {
      const unitPriceEl = priceWrapper?.querySelector('.unit-price .number');
      const unitEl = priceWrapper?.querySelector('.unit-price .unit');
      if (unitPriceEl) unitPriceEl.innerHTML = this.formatMoney(variant.unit_price);
      if (unitEl) {
        unitEl.innerHTML =
          variant.unit_price_measurement.reference_value != 1
            ? variant.unit_price_measurement.reference_value
            : variant.unit_price_measurement.reference_unit;
      }
    }

    const cardPrice = priceWrapper?.querySelector('.card-product-price');
    if (!cardPrice) return;

    if (!cardPrice.querySelector('.compare-price')) {
      const priceSale = document.createElement('div');
      const compareWrap = document.createElement('span');
      const comparePrice = document.createElement('s');
      comparePrice.classList.add('price-item', 'compare-price');
      compareWrap.appendChild(comparePrice);
      priceSale.appendChild(compareWrap);
      priceSale.classList.add('price-regular');
      cardPrice.appendChild(priceSale);
    }

    const compareEl = cardPrice.querySelector('.compare-price');
    const regularEl = cardPrice.querySelector('.price-regular');
    const regularPriceEl = cardPrice.querySelector('.price-regular .price');
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (compareEl) {
      compareEl.innerHTML = onSale ? this.formatMoney(variant.compare_at_price) : '';
      compareEl.setAttribute('data-variant-id', variant.id);
    }
    if (regularEl) {
      regularEl.classList.toggle('primary-color', !!onSale);
      regularEl.setAttribute('data-variant-id', variant.id);
    }
    if (regularPriceEl) {
      regularPriceEl.innerHTML = priceFormat;
      regularPriceEl.classList.toggle('price--special', !!onSale);
      regularPriceEl.setAttribute('data-variant-id', variant.id);
    }
    cardPrice.classList.toggle('price--sold-out', !variant.available);
  }

  updateVariantUrl(variantId) {
    if (!variantId || !window.history?.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    window.history.replaceState({ ...window.history.state }, '', url.toString());
  }

  bindEvents() {
    if (this._nextBtn) {
      this._nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.next();
      });
    }
    if (this._prevBtn) {
      this._prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.prev();
      });
    }

    this._stepPills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const target = parseInt(pill.dataset.step, 10);
        if (Number.isNaN(target)) return;
        if (target === this._step) return;
        if (target < this._step || target <= this._maxReached) {
          this.updateStep(target);
          return;
        }
        this.next();
      });
    });

    // When custom_variants swaps the visible fields on variant change, re-wire inputs.
    document.addEventListener('ps:custom-variants-changed', () => {
      this.wireNameInputs();
      this.applyCurrentFont();
      this.setupSelectInlineLabels();
      this.populateSummary();
      this.recalcTotals();
    });

    // Theme pubsub `variantChange` fires after product-info.js finishes the
    // variant fetch + DOM swap. Single source of truth for variant updates.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        this._currentVariant = event?.data?.variant || null;
        this.populateSummary();
        this.recalcTotals();
        this.syncStep1PriceMirror();
      });
    }
  }

  get step() {
    return this._step;
  }

  next() {
    if (!this.validateCurrentStep()) return;
    if (this._step < this.totalSteps) {
      this.updateStep(this._step + 1);
    } else if (this._form && this._canAddToCart) {
      // On the last step, the Next button doubles as the submit button.
      // Validate all steps first so invalid hidden-panel fields can be shown.
      for (let s = 1; s <= this.totalSteps; s++) {
        const invalid = this.findInvalidField(s);
        if (invalid) {
          this.updateStep(s);
          requestAnimationFrame(() => invalid.reportValidity());
          return;
        }
      }
      this._expectingCartUpdate = true;
      this.submitForm();
    }
  }

  submitForm() {
    if (!this._form) return;
    if (typeof this._form.requestSubmit === 'function') {
      this._form.requestSubmit();
      return;
    }

    let event;
    if (typeof SubmitEvent === 'function') {
      event = new SubmitEvent('submit', { bubbles: true, cancelable: true });
    } else {
      event = document.createEvent('Event');
      event.initEvent('submit', true, true);
    }

    if (this._form.dispatchEvent(event)) {
      HTMLFormElement.prototype.submit.call(this._form);
    }
  }

  prev() {
    if (this._step > 1) {
      this.updateStep(this._step - 1);
    }
  }

  updateStep(n, options = {}) {
    const { scroll = true } = options;
    n = Math.max(1, Math.min(this.totalSteps, n));
    this._step = n;
    if (n > this._maxReached) this._maxReached = n;

    // Panels
    this._panels.forEach((p) => {
      const match = parseInt(p.dataset.step, 10) === n;
      p.setAttribute('data-active', match ? '1' : '0');
    });

    // Step pills
    this._stepPills.forEach((pill) => {
      const idx = parseInt(pill.dataset.step, 10);
      let state = 'todo';
      if (idx < n) state = 'done';
      else if (idx === n) state = 'active';
      pill.setAttribute('data-state', state);

      const numEl = pill.querySelector('[data-step-num]');
      if (numEl) {
        if (idx < n) {
          numEl.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="tw-w-3.5 tw-h-3.5" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
        } else {
          numEl.textContent = String(idx);
        }
      }
    });

    // Separators
    this._seps.forEach((sep) => {
      const idx = parseInt(sep.dataset.sep, 10);
      sep.setAttribute('data-fill', idx < n ? '1' : '0');
    });

    // Nav button visibility + label
    this.updateNavButtons(n);

    // Side-effects on entering steps
    if (n === this.totalSteps) {
      this.populateSummary();
      this.recalcTotals();
      // Defer so the panel layout is settled before measuring description heights
      requestAnimationFrame(() => this.updateUpsellMoreButtons());
    }

    if (scroll) {
      const headerOffset = this.getStickyHeaderOffset();
      // If the stepper header is already visible below the sticky site header
      // and above the viewport bottom, don't scroll — the user can see where
      // they are in the flow and a scroll would feel jarring.
      const stepperHeader = this.querySelector('.ps-header');
      const stepRect = (stepperHeader || this).getBoundingClientRect();
      const stepperVisible = stepRect.top >= headerOffset - 2 && stepRect.top <= window.innerHeight - 40;
      if (!stepperVisible) {
        const top = this.getBoundingClientRect().top + window.scrollY - headerOffset - 12;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    }
  }

  /**
   * Measure how much viewport-top space is occupied by the site's sticky/
   * fixed header, so scroll targets can land BELOW it instead of behind it.
   * Scans candidate header elements + their ancestors for a sticky/fixed
   * container currently pinned to the top of the viewport, and returns that
   * container's bottom-edge Y coordinate.
   */
  getStickyHeaderOffset() {
    const candidates = document.querySelectorAll(
      'header.header-section, .shopify-section-group-header-group, header, sticky-header, [data-sticky-header]',
    );
    let maxBottom = 0;
    const viewportH = window.innerHeight;
    candidates.forEach((el) => {
      // Skip elements inside our own dialog — their ancestors are fixed + inset:0
      // (full viewport) and would get mistaken for a site header.
      if (el.closest('.ps-card-drawer, .ps-preview-overlay')) return;
      /** @type {Element | null} */
      let cur = el;
      while (cur && cur !== document.body) {
        const pos = getComputedStyle(cur).position;
        if (pos === 'sticky' || pos === 'fixed') {
          const rect = cur.getBoundingClientRect();
          // Only count containers that are currently pinned at/near the top
          // AND are reasonably header-sized (< 40% of the viewport) — this
          // rejects full-viewport overlays that happen to contain a <header>.
          if (rect.top <= 4 && rect.bottom > maxBottom && rect.height < viewportH * 0.4) {
            maxBottom = rect.bottom;
          }
          break;
        }
        cur = cur.parentElement;
      }
    });
    return maxBottom;
  }

  updateNavButtons(n) {
    const isFirst = n === 1;
    const isLast = n === this.totalSteps;

    if (this._prevBtn) {
      this._prevBtn.hidden = isFirst;
    }
    if (this._nextLabel) {
      if (isLast) {
        this._nextLabel.textContent = this._atcText;
      } else if (n === this.totalSteps - 1) {
        this._nextLabel.textContent = this._labelReview;
      } else {
        this._nextLabel.textContent = this._labelContinue;
      }
    }
    // SVGElement's .hidden IDL property does not reflect to the hidden HTML
    // attribute, so we must toggle the attribute directly for the CSS to match.
    if (this._nextIconArrow) this._nextIconArrow.toggleAttribute('hidden', isLast);
    if (this._nextIconCart) this._nextIconCart.toggleAttribute('hidden', !isLast);
    if (this._nextBtn) {
      this._nextBtn.toggleAttribute('disabled', isLast && !this._canAddToCart);
    }
  }

  /* ------- Per-step validation ------- */

  setupFormValidation() {
    if (!this._form) return;
    this._form.addEventListener(
      'submit',
      (e) => {
        for (let s = 1; s <= this.totalSteps; s++) {
          const invalid = this.findInvalidField(s);
          if (invalid) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.updateStep(s);
            requestAnimationFrame(() => invalid.reportValidity());
            return;
          }
        }
      },
      true,
    );
  }

  findInvalidField(stepNum) {
    const panel = Array.from(this._panels).find((p) => parseInt(p.dataset.step, 10) === stepNum);
    if (!panel) return null;
    const fields = panel.querySelectorAll(
      'input[required]:not(:disabled), select[required]:not(:disabled), textarea[required]:not(:disabled)',
    );
    for (const field of fields) {
      if (field.type === 'hidden') continue;
      if (!field.checkValidity()) return field;
    }
    return null;
  }

  validateCurrentStep() {
    const invalid = this.findInvalidField(this._step);
    if (invalid) {
      invalid.reportValidity();
      return false;
    }
    return true;
  }

  /* ------- Name input wiring (counter, warn, capitalize) ------- */

  wireNameInputs() {
    const inputs = this.querySelectorAll('[data-ps-text-input]');
    inputs.forEach((input) => {
      if (input._psWired) return;
      input._psWired = true;

      const row = input.closest('.ps-name-row');
      const counter = row ? row.querySelector('[data-ps-counter]') : null;
      const max = parseInt(input.dataset.psMax, 10) || 30;
      const capitalize = input.hasAttribute('data-should-capitalize');

      const update = () => {
        let v = input.value;
        if (v.length > max) v = v.slice(0, max);
        if (capitalize) v = v.replace(/(^|\s)(\S)/g, (_, a, b) => a + b.toUpperCase());
        if (input.value !== v) input.value = v;

        if (counter) counter.textContent = `${v.length}/${max}`;
        if (row) row.setAttribute('data-warn', v.length >= max ? '1' : '0');
      };

      input.addEventListener('input', update);
      update();
    });
  }

  /* ------- Font picker → inputs font sync ------- */

  /**
   * Populate `[data-ps-select-value]` with the currently-selected option's
   * label, and update it whenever the user picks a new pill / dropdown
   * option. Used by the inline "Font: Gothic" style label since each pill
   * no longer prints the value name underneath itself.
   *
   * Idempotent — the wiring marker on each fieldset means re-runs (e.g. on
   * `ps:custom-variants-changed`) only refresh the displayed value rather
   * than double-binding listeners.
   */
  setupSelectInlineLabels() {
    this.querySelectorAll('[data-ps-field]').forEach((field) => {
      const valEl = field.querySelector('[data-ps-select-value]');
      if (!valEl) return;
      if (!field.dataset.psSelectInlineWired) {
        field.dataset.psSelectInlineWired = '1';
        field.querySelectorAll('input[type="radio"][data-ps-select-option]').forEach((radio) => {
          radio.addEventListener('change', () => {
            if (radio.checked) {
              valEl.textContent = radio.dataset.psSelectLabel || radio.value;
            }
          });
        });
        const dropdown = field.querySelector('select[data-ps-select-dropdown]');
        if (dropdown) {
          dropdown.addEventListener('change', () => {
            const opt = dropdown.options[dropdown.selectedIndex];
            valEl.textContent = opt && opt.value ? opt.textContent.trim() : '';
          });
        }
      }
      // Refresh from the current state (covers initial load + re-runs after
      // variant-scoped fields are swapped in/out).
      const checked = field.querySelector('input[type="radio"][data-ps-select-option]:checked');
      if (checked) {
        valEl.textContent = checked.dataset.psSelectLabel || checked.value;
        return;
      }
      const dropdown = field.querySelector('select[data-ps-select-dropdown]');
      if (dropdown) {
        const opt = dropdown.options[dropdown.selectedIndex];
        valEl.textContent = opt && opt.value ? opt.textContent.trim() : '';
      }
    });
  }

  wireFontPicker() {
    const fields = this.querySelectorAll('[data-ps-field][data-ps-field-id="Font"]');
    fields.forEach((field) => {
      const radios = field.querySelectorAll('input[type="radio"]');
      radios.forEach((radio) => {
        if (radio._psFontWired) return;
        radio._psFontWired = true;
        radio.addEventListener('change', () => {
          if (radio.checked) this.applyFont(radio.value);
        });
      });
    });
    this.applyCurrentFont();
  }

  applyCurrentFont() {
    const field = this.querySelector('[data-ps-field][data-ps-field-id="Font"]');
    if (!field) return;
    const checked = field.querySelector('input[type="radio"]:checked') || field.querySelector('input[type="radio"]');
    if (checked) this.applyFont(checked.value);
  }

  applyFont(fontName) {
    if (!fontName) return;
    const known = ['Centuary', 'Century', 'Gothic', 'Dancing', 'Noty', 'Tidy', 'Buffalo'];
    if (!known.includes(fontName)) return;

    // Centuary is a legacy misspelling of Century kept in merchant data.
    const cssFont = fontName === 'Centuary' ? 'Century' : fontName;

    this.querySelectorAll('.ps-name-input').forEach((input) => {
      input.style.fontFamily = `'${cssFont}', sans-serif`;
      if (fontName === 'Dancing') {
        input.classList.remove('tw-uppercase');
        input.setAttribute('data-no-cyrillic', 'true');
      } else {
        input.removeAttribute('data-no-cyrillic');
        if (input.hasAttribute('data-should-capitalize')) {
          input.classList.add('tw-uppercase');
        }
      }
    });
  }

  /* ------- Summary + totals ------- */

  populateSummary() {
    const host = this.querySelector('[data-ps-summary]');
    if (!host) return;

    // Line 1: product title + selected variant values
    const parts = [];
    const title = this.dataset.productTitle;
    if (title) parts.push(title);
    if (this._variantSelects) {
      this._variantSelects.querySelectorAll('[data-selected-value]').forEach((el) => {
        const v = (el.textContent || '').trim();
        if (v) parts.push(v);
      });
    }

    // Line 2: engraved name values, rendered in the selected font.
    const props = this.collectProperties();
    const nameValues = props.filter((p) => /^\d+\./.test(p.key) || /name/i.test(p.key)).map((p) => p.value);

    // Find the selected font so we can style the engraving preview
    let fontFamily = '';
    const fontField = this.querySelector('[data-ps-field][data-ps-field-id="Font"]');
    if (fontField) {
      const checked = fontField.querySelector('input[type="radio"]:checked');
      if (checked) {
        const v = checked.value === 'Centuary' ? 'Century' : checked.value;
        fontFamily = `'${v}', sans-serif`;
      }
    }

    const html = [];
    if (parts.length) {
      html.push(`<div class="ps-sum-piece">${this.escapeHTML(parts.join(' · '))}</div>`);
    }
    if (nameValues.length) {
      html.push(
        `<div class="ps-sum-engraving" style="font-family: ${fontFamily};">${this.escapeHTML(
          nameValues.join(' · '),
        )}</div>`,
      );
    }
    host.innerHTML = html.join('');
  }

  collectProperties() {
    if (!this._form) return [];
    const seen = new Set();
    const rows = [];
    const inputs = this._form.querySelectorAll(
      'input[name^="properties["]:not(:disabled), select[name^="properties["]:not(:disabled), textarea[name^="properties["]:not(:disabled)',
    );
    inputs.forEach((input) => {
      const name = input.name;
      if (seen.has(name)) return;
      if (input.type === 'radio' && !input.checked) return;
      const value = input.value;
      if (!value || input.type === 'hidden' || input.type === 'file') return;
      seen.add(name);
      const match = name.match(/properties\[(.+?)\]/);
      const key = match ? match[1] : name;
      rows.push({ key, value });
    });
    return rows;
  }

  currentVariantPrice() {
    // Post-variant-change: the variant object from `variantChange` pubsub.
    if (this._currentVariant && typeof this._currentVariant.price === 'number') {
      return this._currentVariant.price;
    }
    // Initial render: the server-rendered selected-variant JSON in <variant-selects>.
    const script = this._variantSelects?.querySelector('script[data-selected-variant]');
    if (script) {
      try {
        const variant = JSON.parse(script.textContent);
        if (variant && typeof variant.price === 'number') return variant.price;
      } catch (e) {
        /* ignore */
      }
    }
    return 0;
  }

  /**
   * Parse a money-formatted string into integer cents. Handles "$1,234.56",
   * "€1.234,56", "51.25", "51,25", etc. by detecting which separator is the
   * decimal (the rightmost one).
   */
  parseMoney(text) {
    if (!text) return null;
    const cleaned = text.replace(/[^\d.,-]/g, '').trim();
    if (!cleaned) return null;
    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    let normalized;
    if (lastDot === -1 && lastComma === -1) {
      normalized = cleaned;
    } else if (lastDot > lastComma) {
      normalized = cleaned.replace(/,/g, '');
    } else {
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    }
    const parsed = parseFloat(normalized);
    if (Number.isNaN(parsed)) return null;
    return Math.round(parsed * 100);
  }

  currentVariantPriceMoney() {
    const cents = this.currentVariantPrice();
    return this.formatMoney(cents);
  }

  formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      const fmt = (window.theme && window.theme.moneyFormat) || window.Shopify.money_format || '${{amount}}';
      return window.Shopify.formatMoney(cents, fmt);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  recalcTotals(options = {}) {
    const { animate = true } = options;
    const piece = this.currentVariantPrice();
    const extras = this.getSelectedUpsells().reduce((sum, u) => sum + (u.price || 0), 0);
    const grand = piece + extras;

    const pieceEl = this.querySelector('[data-ps-total-piece]');
    const extrasEl = this.querySelector('[data-ps-total-extras]');
    const grandEl = this.querySelector('[data-ps-total-grand]');

    if (pieceEl) pieceEl.textContent = this.formatMoney(piece);
    if (extrasEl) extrasEl.textContent = this.formatMoney(extras);
    if (grandEl) {
      if (animate && this._lastGrand !== null && this._lastGrand !== grand) {
        this.animatePrice(grandEl, this._lastGrand, grand);
      } else {
        grandEl.textContent = this.formatMoney(grand);
      }
    }
    this._lastGrand = grand;
  }

  animatePrice(el, from, to) {
    if (from === to) {
      el.textContent = this.formatMoney(to);
      return;
    }
    const dur = 420;
    const t0 = performance.now();
    el.classList.add('bump');
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = Math.round(from + (to - from) * eased);
      el.textContent = this.formatMoney(v);
      if (p < 1) requestAnimationFrame(tick);
      else setTimeout(() => el.classList.remove('bump'), 120);
    };
    requestAnimationFrame(tick);
  }

  escapeHTML(s) {
    const d = document.createElement('div');
    d.textContent = String(s);
    return d.innerHTML;
  }

  /* ------- Upsells ------- */

  setupUpsells() {
    const cards = this.querySelectorAll('[data-upsell-card]');
    if (!cards.length) return;

    cards.forEach((card) => {
      const checkbox = card.querySelector('.ps-upsell-checkbox');
      if (checkbox) {
        checkbox.addEventListener('change', () => {
          this.recalcTotals();
        });
      }

      // Expand/collapse long descriptions. The button is inside the <label>,
      // so prevent the click from toggling the checkbox.
      const moreBtn = card.querySelector('[data-ps-upsell-more]');
      if (moreBtn) {
        moreBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const expanded = card.classList.toggle('is-expanded');
          moreBtn.textContent = expanded ? this._labelLess : this._labelMore;
        });
      }
    });

    this.hideUpsellsAlreadyInCart();
    this.setupFetchInterception();
  }

  /**
   * Show "More" only when descriptions actually overflow. Must run when the
   * upsell cards are visible — i.e. when the finish panel is active, since the
   * elements have 0 height while display:none.
   */
  updateUpsellMoreButtons() {
    this.querySelectorAll('[data-upsell-card]').forEach((card) => {
      const desc = card.querySelector('[data-ps-upsell-desc]');
      const moreBtn = card.querySelector('[data-ps-upsell-more]');
      if (!desc || !moreBtn) return;
      // Only measure when the card isn't already expanded (scrollHeight == clientHeight).
      if (card.classList.contains('is-expanded')) return;
      moreBtn.hidden = desc.scrollHeight <= desc.clientHeight + 1;
    });
  }

  async hideUpsellsAlreadyInCart() {
    const cardsToCheck = this.querySelectorAll('[data-upsell-card][data-upsell-hide-if-in-cart="true"]');
    if (!cardsToCheck.length) return;

    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      const variantIds = new Set((cart.items || []).map((i) => i.variant_id));
      const productIds = new Set((cart.items || []).map((i) => i.product_id));

      cardsToCheck.forEach((card) => {
        const vId = parseInt(card.dataset.upsellVariantId, 10);
        const pId = parseInt(card.dataset.upsellProductId, 10);
        if (variantIds.has(vId) || productIds.has(pId)) {
          card.hidden = true;
          const checkbox = card.querySelector('.ps-upsell-checkbox');
          if (checkbox) checkbox.checked = false;
        }
      });
    } catch (e) {
      /* silent */
    }
  }

  getSelectedUpsells() {
    const selected = [];
    this.querySelectorAll('[data-upsell-card]').forEach((card) => {
      if (card.hidden) return;
      const checkbox = card.querySelector('.ps-upsell-checkbox');
      if (!(checkbox && checkbox.checked)) return;

      const entry = {
        variantId: card.dataset.upsellVariantId,
        price: parseInt(card.dataset.upsellPrice || '0', 10),
        nested: card.dataset.upsellNested !== 'false',
      };

      const drawerId = card.dataset.psDrawerId;
      const drawer = drawerId ? document.getElementById(drawerId) : null;
      const fieldset = drawer
        ? drawer.querySelector('[data-ps-personalized-upsell-fields]')
        : card.querySelector('[data-ps-personalized-upsell-fields]');
      if (fieldset) {
        const properties = {};
        fieldset
          .querySelectorAll(
            'input[name^="properties["]:not(:disabled), select[name^="properties["]:not(:disabled), textarea[name^="properties["]:not(:disabled)',
          )
          .forEach((el) => {
            if (el.type === 'radio' && !el.checked) return;
            const m = el.name.match(/properties\[(.+?)\]/);
            if (m && el.value) properties[m[1]] = el.value;
          });
        if (Object.keys(properties).length > 0) entry.properties = properties;
      }

      selected.push(entry);
    });
    return selected;
  }

  /* ------- Jewelry preview (compact canvas + click-to-expand overlay) ------- */

  setupJewelryPreview() {
    const compactPreviews = this.querySelectorAll('[data-ps-compact-preview]');
    const galleryZoomTriggers = this.querySelectorAll('[data-ps-gallery-zoom]');
    if (!compactPreviews.length && !galleryZoomTriggers.length) return;

    const sectionId = (this.productFormId || '').replace(/^product-form-/, '');
    const defaultOverlayId = `ps-preview-overlay-${sectionId}`;
    const overlays = new Map();

    const getOverlay = (overlayId) => {
      const id = overlayId || defaultOverlayId;
      if (!id) return null;
      if (overlays.has(id)) return overlays.get(id);

      const overlayEl = document.getElementById(id);
      if (!overlayEl) {
        overlays.set(id, null);
        return null;
      }
      if (overlayEl.parentElement !== document.body) {
        document.body.appendChild(overlayEl);
      }

      const overlay = {
        el: overlayEl,
        content: overlayEl.querySelector('.ps-preview-overlay__content'),
        img: overlayEl.querySelector('.ps-preview-overlay__img'),
        closeBtn: overlayEl.querySelector('.ps-preview-overlay__close'),
        backdrop: overlayEl.querySelector('.ps-preview-overlay__backdrop'),
      };

      const closeOverlay = () => {
        overlay.el.classList.remove('is-open');
        overlay.el.setAttribute('aria-hidden', 'true');
      };
      overlay['close'] = closeOverlay;

      if (overlay.closeBtn) overlay.closeBtn.addEventListener('click', closeOverlay);
      if (overlay.backdrop) overlay.backdrop.addEventListener('click', closeOverlay);

      overlays.set(id, overlay);
      return overlay;
    };

    const getGalleryItems = (sourceImg) => {
      const gallery = sourceImg?.closest?.('[data-ps-gallery]');
      if (!gallery) return [];

      return [...gallery.querySelectorAll('.ps-card-gallery__slide')]
        .map((slide) => {
          const img = slide.querySelector('img.jp2d-preview-img, img.ps-card-gallery__img, img');
          const src = img?.getAttribute?.('data-ps-gallery-full-src') || img?.getAttribute?.('src');
          if (!img || !src) return null;
          return {
            src,
            alt: img.getAttribute('alt') || '',
            sourceImg: img,
          };
        })
        .filter(Boolean);
    };

    const ensureOverlayGallery = (overlay) => {
      if (!overlay?.content) return null;
      let gallery = overlay.content.querySelector('[data-ps-overlay-gallery]');
      if (gallery) return gallery;

      gallery = document.createElement('div');
      gallery.className = 'ps-preview-overlay__gallery';
      gallery.setAttribute('data-ps-overlay-gallery', '');
      gallery.innerHTML =
        '<div class="ps-preview-overlay__track" data-ps-overlay-track></div><div class="ps-preview-overlay__thumbs" data-ps-overlay-thumbs></div>';
      overlay.content.appendChild(gallery);
      return gallery;
    };

    const openOverlayGallery = (overlay, sourceImg, items) => {
      const gallery = ensureOverlayGallery(overlay);
      const track = gallery?.querySelector('[data-ps-overlay-track]');
      const thumbs = gallery?.querySelector('[data-ps-overlay-thumbs]');
      if (!gallery || !track || !thumbs) return false;

      const activeIndex = Math.max(
        0,
        items.findIndex((item) => item.sourceImg === sourceImg),
      );
      let overlayActiveIndex = activeIndex;
      track.innerHTML = '';
      thumbs.innerHTML = '';

      items.forEach((item, index) => {
        const slide = document.createElement('div');
        slide.className = 'ps-preview-overlay__slide';
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', `${index + 1} / ${items.length}`);

        const img = document.createElement('img');
        img.src = item.src;
        img.alt = item.alt;
        img.loading = index === activeIndex ? 'eager' : 'lazy';
        img.draggable = false;
        slide.appendChild(img);
        track.appendChild(slide);

        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = `ps-preview-overlay__thumb${index === activeIndex ? ' is-active' : ''}`;
        thumb.setAttribute('aria-label', `${index + 1} / ${items.length}`);
        if (index === activeIndex) thumb.setAttribute('aria-current', 'true');

        const thumbImg = document.createElement('img');
        thumbImg.src = item.sourceImg?.getAttribute('src') || item.src;
        thumbImg.alt = item.alt;
        thumb.appendChild(thumbImg);
        thumb.addEventListener('click', () => {
          slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        });
        thumbs.appendChild(thumb);
      });

      let scrollFrame = null;
      const getClosestOverlayIndex = () => {
        const trackLeft = track.getBoundingClientRect().left;
        const slides = [...track.querySelectorAll('.ps-preview-overlay__slide')];
        return slides.reduce(
          (closest, slide, index) => {
            const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
            return distance < closest.distance ? { index, distance } : closest;
          },
          { index: overlayActiveIndex, distance: Infinity },
        ).index;
      };
      const scrollOverlayTo = (index, behavior = 'smooth') => {
        const slide = track.querySelectorAll('.ps-preview-overlay__slide')[index];
        if (!slide) return;
        track.scrollTo({
          left: slide['offsetLeft'] - track['offsetLeft'],
          behavior,
        });
      };
      const syncOverlayThumbs = () => {
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          const nextIndex = getClosestOverlayIndex();
          overlayActiveIndex = nextIndex;

          [...thumbs.querySelectorAll('.ps-preview-overlay__thumb')].forEach((thumb, index) => {
            const isActive = index === nextIndex;
            thumb.classList.toggle('is-active', isActive);
            if (isActive) {
              thumb.setAttribute('aria-current', 'true');
              thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
            } else {
              thumb.removeAttribute('aria-current');
            }
          });
        });
      };
      track.addEventListener('scroll', syncOverlayThumbs, { passive: true, once: false });
      let dragStartX = 0;
      let dragStartScrollLeft = 0;
      let isDragging = false;
      let hasDragged = false;
      let suppressOverlayClickUntil = 0;

      track.onpointerdown = (event) => {
        if (event['button'] != null && event['button'] !== 0) return;
        isDragging = true;
        hasDragged = false;
        dragStartX = event['clientX'] || 0;
        dragStartScrollLeft = track.scrollLeft;
      };
      track.onpointermove = (event) => {
        if (!isDragging) return;
        const currentX = event['clientX'] || dragStartX;
        if (!hasDragged && Math.abs(currentX - dragStartX) > 3) {
          hasDragged = true;
          track.classList.add('is-dragging');
          track.setPointerCapture?.(event['pointerId']);
        }
        if (!hasDragged) return;
        track.scrollLeft = dragStartScrollLeft - (currentX - dragStartX);
        event.preventDefault();
      };
      track.onpointerup = (event) => {
        if (!isDragging) return;
        isDragging = false;
        track.classList.remove('is-dragging');
        track.releasePointerCapture?.(event['pointerId']);
        if (!hasDragged) return;
        suppressOverlayClickUntil = Date.now() + 350;
        const closestIndex = getClosestOverlayIndex();
        overlayActiveIndex = closestIndex;
        scrollOverlayTo(closestIndex);
        syncOverlayThumbs();
      };
      track.onpointercancel = track.onpointerup;
      track.onclick = (event) => {
        if (Date.now() <= suppressOverlayClickUntil) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        if (event.target?.closest?.('img')) return;
        overlay['close']?.();
      };
      thumbs.onclick = (event) => {
        if (event.target?.closest?.('.ps-preview-overlay__thumb')) return;
        overlay['close']?.();
      };

      overlay.content.classList.add('has-gallery');
      if (overlay.img) overlay.img.setAttribute('hidden', '');
      gallery.removeAttribute('hidden');
      overlay.el.classList.add('is-open');
      overlay.el.setAttribute('aria-hidden', 'false');

      requestAnimationFrame(() => {
        const activeSlide = track.querySelectorAll('.ps-preview-overlay__slide')[activeIndex];
        activeSlide?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
        syncOverlayThumbs();
      });

      return true;
    };

    const openOverlay = (overlay, sourceImg) => {
      if (!overlay) return;
      const galleryItems = getGalleryItems(sourceImg);
      if (galleryItems.length > 1 && openOverlayGallery(overlay, sourceImg, galleryItems)) return;

      overlay.content?.classList.remove('has-gallery');
      const gallery = overlay.content?.querySelector('[data-ps-overlay-gallery]');
      if (gallery) gallery.setAttribute('hidden', '');
      const src = sourceImg?.getAttribute?.('src');
      if (src && overlay.img) {
        overlay.img.removeAttribute('hidden');
        overlay.img.setAttribute('src', src);
      }
      overlay.el.classList.add('is-open');
      overlay.el.setAttribute('aria-hidden', 'false');
    };

    this._compactInstances = [];

    compactPreviews.forEach((preview) => {
      const canvas = preview.querySelector('.jp2d-compact-canvas');
      const sourceImg = preview.querySelector('.jp2d-preview-img');
      const formId = preview.dataset.psPreviewFormId || sourceImg?.dataset.formId || this.productFormId;
      const form = formId ? document.getElementById(formId) : this._form;
      const configScope =
        preview.closest('[data-ps-gallery]') ||
        preview.closest('.ps-card-drawer') ||
        preview.closest('.ps-card-preview');
      const configScript =
        preview.querySelector('script.jp2d-compact-config') ||
        configScope?.querySelector('script.jp2d-compact-config') ||
        preview.parentElement?.querySelector('script.jp2d-compact-config') ||
        this.querySelector('script.jp2d-compact-config');
      let compactData = null;

      if (configScript) {
        try {
          compactData = JSON.parse(configScript.textContent);
        } catch (e) {
          /* ignore */
        }
      }

      if (!this._compactData && compactData) this._compactData = compactData;
      const overlay = getOverlay(preview.dataset.psPreviewOverlayId);

      if (sourceImg) {
        const previewThumb = preview.closest('[data-ps-gallery]')?.querySelector('[data-ps-gallery-preview-thumb]');
        const syncPreviewThumb = () => {
          const previewSrc = sourceImg.getAttribute('src');
          if (previewThumb && previewSrc) previewThumb.setAttribute('src', previewSrc);
        };
        const observer = new MutationObserver(() => {
          this.renderCompactView(canvas, sourceImg, { compactData, form });
          syncPreviewThumb();
        });
        observer.observe(sourceImg, { attributes: true, attributeFilter: ['src'] });
        sourceImg.addEventListener('load', syncPreviewThumb);
        syncPreviewThumb();
      }
      this.renderCompactView(canvas, sourceImg, { compactData, form });

      if (overlay) {
        preview.addEventListener('click', () => openOverlay(overlay, sourceImg));
        preview.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openOverlay(overlay, sourceImg);
          }
        });
      }

      this._compactInstances.push({ preview, canvas, sourceImg, compactData, form, overlay });
    });

    galleryZoomTriggers.forEach((trigger) => {
      const sourceImg = trigger.matches('img')
        ? trigger
        : trigger.querySelector('img.jp2d-preview-img, img.product-media__image, img');
      const overlay = getOverlay(trigger.dataset.psPreviewOverlayId);
      if (!sourceImg || !overlay) return;

      const previewThumb = trigger.closest('[data-ps-gallery]')?.querySelector('[data-ps-gallery-preview-thumb]');
      const syncPreviewThumb = () => {
        const previewSrc = sourceImg.getAttribute('src');
        if (previewThumb && previewSrc) previewThumb.setAttribute('src', previewSrc);
      };
      if (sourceImg.classList.contains('jp2d-preview-img')) {
        new MutationObserver(syncPreviewThumb).observe(sourceImg, {
          attributes: true,
          attributeFilter: ['src'],
        });
        sourceImg.addEventListener('load', syncPreviewThumb);
        syncPreviewThumb();
      }

      trigger.addEventListener('click', () => openOverlay(overlay, sourceImg));
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openOverlay(overlay, sourceImg);
        }
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        overlays.forEach((overlay) => {
          if (!overlay?.el.classList.contains('is-open')) return;
          overlay.el.classList.remove('is-open');
          overlay.el.setAttribute('aria-hidden', 'true');
        });
      }
    });

    this.querySelectorAll('[data-ps-preview-trigger]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const overlay = getOverlay(btn.dataset.psPreviewOverlayId);
        const latest =
          this._compactInstances.find((i) => i.overlay === overlay && i.sourceImg?.src)?.sourceImg ||
          this._compactInstances.find((i) => i.sourceImg?.src)?.sourceImg ||
          this._compactInstances[0]?.sourceImg;
        openOverlay(overlay, latest);
      });
    });
  }

  setupPersonalizedUpsellGalleries() {
    this.querySelectorAll('[data-ps-gallery]').forEach((gallery) => {
      if (gallery.getAttribute('data-ps-gallery-wired') === 'true') return;
      gallery.setAttribute('data-ps-gallery-wired', 'true');

      const mainEl = gallery.querySelector('.swiper-main');
      const wrapper = mainEl?.querySelector('.swiper-wrapper');
      const slides = wrapper ? [...wrapper.querySelectorAll('.swiper-slide')] : [];
      const thumbs = [...gallery.querySelectorAll('[data-ps-gallery-thumb]')];
      const prevBtn = gallery.querySelector('.ps-card-gallery__buttons .swiper-button-prev');
      const nextBtn = gallery.querySelector('.ps-card-gallery__buttons .swiper-button-next');
      if (!mainEl || !wrapper || thumbs.length <= 1) return;

      wrapper.querySelectorAll('img').forEach((img) => {
        img.draggable = false;
      });

      let activeIndex = 0;
      const clampIndex = (index) => Math.max(0, Math.min(thumbs.length - 1, index));
      const getClosestIndex = () => {
        const wrapperLeft = wrapper.getBoundingClientRect().left;
        return slides.reduce(
          (closest, slide, index) => {
            const distance = Math.abs(slide.getBoundingClientRect().left - wrapperLeft);
            return distance < closest.distance ? { index, distance } : closest;
          },
          { index: activeIndex, distance: Infinity },
        ).index;
      };
      const syncTo = (index, options = {}) => {
        const nextIndex = clampIndex(index);
        activeIndex = nextIndex;
        wrapper.removeAttribute('style');
        slides.forEach((slide, i) => {
          slide.classList.toggle('swiper-slide-active', i === nextIndex);
          slide.classList.toggle('swiper-slide-prev', i === nextIndex - 1);
          slide.classList.toggle('swiper-slide-next', i === nextIndex + 1);
          slide.setAttribute('aria-label', `${i + 1} / ${slides.length}`);
          slide.setAttribute('role', 'group');
        });
        thumbs.forEach((thumb, i) => {
          const isActive = i === nextIndex;
          thumb.classList.toggle('is-active', isActive);
          if (isActive) {
            thumb.setAttribute('aria-current', 'true');
            if (options.scrollThumb !== false) {
              thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
            }
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
        prevBtn?.classList.toggle('swiper-button-disabled', nextIndex === 0);
        nextBtn?.classList.toggle('swiper-button-disabled', nextIndex === thumbs.length - 1);
        prevBtn?.setAttribute('aria-disabled', nextIndex === 0 ? 'true' : 'false');
        nextBtn?.setAttribute('aria-disabled', nextIndex === thumbs.length - 1 ? 'true' : 'false');
      };

      const slideTo = (index, speed = 300) => {
        const nextIndex = clampIndex(index);
        syncTo(nextIndex);
        const slide = slides[nextIndex];
        if (slide) {
          wrapper.scrollTo({
            left: slide['offsetLeft'] - wrapper['offsetLeft'],
            behavior: speed === 0 ? 'auto' : 'smooth',
          });
        }
      };

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (event) => {
          event.preventDefault();
          const index = parseInt(thumb.getAttribute('data-index') || '0', 10);
          slideTo(Number.isNaN(index) ? 0 : index);
        });
      });

      if (prevBtn) {
        prevBtn.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            slideTo(activeIndex - 1);
          },
          true,
        );
      }
      if (nextBtn) {
        nextBtn.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            slideTo(activeIndex + 1);
          },
          true,
        );
      }

      let scrollSyncFrame = null;
      wrapper.addEventListener(
        'scroll',
        () => {
          if (scrollSyncFrame) cancelAnimationFrame(scrollSyncFrame);
          scrollSyncFrame = requestAnimationFrame(() => {
            const closestIndex = getClosestIndex();
            syncTo(closestIndex, { scrollThumb: false });
          });
        },
        { passive: true },
      );

      let dragStartX = 0;
      let dragStartScrollLeft = 0;
      let isDragging = false;
      let hasDragged = false;
      let suppressClickUntil = 0;

      wrapper.addEventListener(
        'pointerdown',
        (event) => {
          if (event['button'] != null && event['button'] !== 0) return;
          isDragging = true;
          hasDragged = false;
          dragStartX = event['clientX'] || 0;
          dragStartScrollLeft = wrapper.scrollLeft;
        },
        false,
      );
      wrapper.addEventListener(
        'pointermove',
        (event) => {
          if (!isDragging) return;
          const currentX = event['clientX'] || dragStartX;
          if (!hasDragged && Math.abs(currentX - dragStartX) > 3) {
            hasDragged = true;
            wrapper.classList.add('is-dragging');
            wrapper.setPointerCapture?.(event['pointerId']);
          }
          if (!hasDragged) return;
          wrapper.scrollLeft = dragStartScrollLeft - (currentX - dragStartX);
          event.preventDefault();
        },
        false,
      );
      const endDrag = (event) => {
        if (!isDragging) return;
        isDragging = false;
        wrapper.classList.remove('is-dragging');
        wrapper.releasePointerCapture?.(event['pointerId']);
        if (!hasDragged) return;
        suppressClickUntil = Date.now() + 350;
        slideTo(getClosestIndex());
      };
      wrapper.addEventListener('pointerup', endDrag, false);
      wrapper.addEventListener('pointercancel', endDrag, false);
      wrapper.addEventListener(
        'click',
        (event) => {
          if (Date.now() > suppressClickUntil) return;
          event.preventDefault();
          event.stopPropagation();
        },
        true,
      );

      const bindSwiper = () => syncTo(activeIndex, { scrollThumb: false });

      gallery['_psBindSwiper'] = bindSwiper;
      gallery['_psGoToGalleryIndex'] = slideTo;
      syncTo(0, { scrollThumb: false });
    });
  }

  setupPersonalizedUpsellPreviewButtons() {
    this.querySelectorAll('[data-ps-upsell-preview-button]').forEach((button) => {
      if (button.getAttribute('data-ps-preview-button-wired') === 'true') return;
      button.setAttribute('data-ps-preview-button-wired', 'true');

      button.addEventListener('click', () => {
        const drawer = button.closest('[data-ps-card-drawer]');
        if (!drawer) return;
        const body = drawer.querySelector('.ps-card-drawer__body');
        const gallery = drawer.querySelector('[data-ps-gallery]');
        const preview = drawer.querySelector('[data-ps-gallery-zoom], [data-ps-compact-preview]');

        if (gallery && typeof gallery['_psGoToGalleryIndex'] === 'function') {
          gallery['_psGoToGalleryIndex'](0, 300);
        }

        const targetTop = gallery || preview;
        if (body && targetTop) {
          const top = targetTop.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop;
          body.scrollTo({ top: Math.max(0, top - 4), behavior: 'smooth' });
        } else if (targetTop) {
          targetTop.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  getCompactView(compactData = this._compactData, form = this._form) {
    if (!compactData) return null;
    const variantId = form?.querySelector('input[name="id"]')?.value;
    const scene = (variantId && compactData[variantId]) || compactData[Object.keys(compactData)[0]];
    return scene?.compactView || null;
  }

  renderCompactView(canvas, sourceImg, options = {}) {
    if (!canvas || !sourceImg) return;

    const img = sourceImg;

    const doRender = () => {
      // Fetch compactView at draw time, not at call time — this way if
      // _compactData parsed AFTER renderCompactView was first called (race
      // between snippet init order on slow loads), the correct crop is used.
      const compactView = this.getCompactView(options.compactData, options.form);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      if (!compactView) {
        // No crop — show the full image at its natural aspect.
        canvas.width = img.naturalWidth || 400;
        canvas.height = img.naturalHeight || 400;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        return;
      }

      // Crop region from percentages
      const srcX = (compactView.x / 100) * img.naturalWidth;
      const srcY = (compactView.y / 100) * img.naturalHeight;
      const srcW = (compactView.width / 100) * img.naturalWidth;
      const srcH = (compactView.height / 100) * img.naturalHeight;
      const rotation = compactView.rotation || 0;

      const isRotated90 = rotation === 90 || rotation === 270;
      const outW = isRotated90 ? Math.round(srcH) : Math.round(srcW);
      const outH = isRotated90 ? Math.round(srcW) : Math.round(srcH);

      canvas.width = outW;
      canvas.height = outH;

      ctx.save();
      ctx.translate(outW / 2, outH / 2);
      ctx.rotate((rotation * Math.PI) / 180);

      const drawW = isRotated90 ? outH : outW;
      const drawH = isRotated90 ? outW : outH;
      ctx.drawImage(img, srcX, srcY, srcW, srcH, -drawW / 2, -drawH / 2, drawW, drawH);
      ctx.restore();
    };

    if (img.complete && img.naturalWidth > 0) {
      doRender();
    } else {
      img.addEventListener('load', doRender, { once: true });
    }
  }

  /* ------- Success-screen upsells (post-purchase, direct /cart/add) ------- */

  setupSuccessUpsells() {
    const cards = this.querySelectorAll('[data-ps-success-upsell]');
    cards.forEach((card) => this.wireSuccessUpsell(card));
  }

  wireSuccessUpsell(card) {
    // (Teleport already handled by `teleportBlocks()` in the constructor.)

    // Video: click play → play + hide play button + enable native controls.
    const video = card.querySelector('[data-ps-video]');
    const playBtn = card.querySelector('[data-ps-video-play]');
    if (video && playBtn) {
      playBtn.addEventListener('click', () => {
        video.play();
        playBtn.hidden = true;
        video.setAttribute('controls', '');
      });
      video.addEventListener('ended', () => {
        playBtn.hidden = false;
        video.removeAttribute('controls');
      });
    }

    // Dismiss (X and "No thanks") — hide the whole card.
    card.querySelectorAll('[data-ps-success-upsell-dismiss]').forEach((btn) => {
      btn.addEventListener('click', () => {
        card.hidden = true;
        if (video && !video.paused) video.pause();
      });
    });

    // Personalize → open drawer (reusing the drawer shell).
    const drawerId = card.dataset.psDrawerId;
    const drawer = drawerId ? document.getElementById(drawerId) : null;
    if (!drawer) return;

    // Reparent to <body> to escape any transformed / overflow-clipped ancestor.
    if (drawer.parentElement !== document.body) {
      drawer.classList.add('personalization-steps-block');
      this.inheritBlockCustomProperties(drawer);
      document.body.appendChild(drawer);
    }

    const openBtn = card.querySelector('[data-ps-success-upsell-open]');
    if (openBtn) {
      openBtn.addEventListener('click', () => this.openPersonalizedDrawer(drawer));
    }

    // Continue → direct /cart/add.js (no batching with main submit).
    const ctaBtn = card.querySelector('[data-ps-success-upsell-open]');
    const dismissBtns = card.querySelectorAll('[data-ps-success-upsell-dismiss]');
    drawer.querySelectorAll('[data-ps-drawer-confirm]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const containerId = card.dataset.psCardFormId;
        const container = containerId ? document.getElementById(containerId) : drawer;
        const invalid = container
          ? [...container.querySelectorAll('input, select, textarea')].find(
              (el) => !el.disabled && typeof el.checkValidity === 'function' && !el.checkValidity(),
            )
          : null;
        if (invalid) {
          invalid.reportValidity();
          return;
        }
        // Loading state on the card's Personalize button + on the drawer's own
        // Continue button so the user sees the request in flight either way.
        btn.classList.add('loading');
        if (ctaBtn) ctaBtn.classList.add('loading');
        const ok = await this.directAddSuccessUpsellToCart(card, drawer);
        btn.classList.remove('loading');
        if (ctaBtn) ctaBtn.classList.remove('loading');
        if (ok) {
          this.closePersonalizedDrawer(drawer);
          if (ctaBtn) {
            ctaBtn.disabled = true;
            const label = ctaBtn.querySelector('[data-ps-cta-label]');
            if (label) label.textContent = ctaBtn.dataset.psCtaAdded || 'Added to cart';
          }
          dismissBtns.forEach((el) => {
            el.hidden = true;
          });
          if (video && !video.paused) video.pause();
        }
      });
    });

    // Cancel / X / backdrop — close without saving.
    drawer.querySelectorAll('[data-ps-drawer-cancel]').forEach((el) => {
      el.addEventListener('click', () => this.closePersonalizedDrawer(drawer));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        this.closePersonalizedDrawer(drawer);
      }
    });

    this.wirePersonalizedVariantRadios(card, drawer);
  }

  async directAddSuccessUpsellToCart(card, drawer) {
    const containerId = card.dataset.psCardFormId;
    const container = containerId ? document.getElementById(containerId) : null;
    const hiddenInput = container?.querySelector('[data-ps-card-variant-id-input]');
    const variantId = parseInt(hiddenInput?.value || card.dataset.upsellVariantId || '0', 10);
    if (!variantId) return false;

    // Collect merchant-custom properties from the drawer fieldset.
    const properties = {};
    const fieldset = drawer.querySelector('[data-ps-personalized-upsell-fields]');
    if (fieldset) {
      fieldset
        .querySelectorAll(
          'input[name^="properties["]:not(:disabled), select[name^="properties["]:not(:disabled), textarea[name^="properties["]:not(:disabled)',
        )
        .forEach((el) => {
          if (el.type === 'radio' && !el.checked) return;
          if (el.type === 'checkbox' && !el.checked) return;
          const m = el.name.match(/^properties\[(.+)\]$/);
          if (m && el.value) properties[m[1]] = el.value;
        });
    }

    const item = { id: variantId, quantity: 1 };
    const nested = card.dataset.upsellNested === 'true';
    const baseProps = { _channel: 'success_upsell', _upsell: 'true' };
    if (nested) {
      // Attach to the MAIN product variant we submitted earlier.
      const mainId = this._form?.querySelector('input[name="id"]')?.value;
      if (mainId) {
        item.parent_id = parseInt(mainId, 10);
        baseProps._for_variant_id = mainId;
      }
    }
    const mergedProps = { ...baseProps, ...properties };
    if (Object.keys(mergedProps).length > 0) item.properties = mergedProps;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [item],
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (data.status) {
        /* error response */
        console.warn('Success-upsell add error:', data);
        return false;
      }

      // Refresh cart drawer + icon bubble DOM without opening the drawer.
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data, { open: false });
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'ps-success-upsell',
          productVariantId: String(variantId),
          cartData: data,
        });
      }
      return true;
    } catch (e) {
      console.error('Success-upsell add failed', e);
      return false;
    }
  }

  /* ------- Personalized-card upsells (dialog-based complex upsells) ------- */

  setupPersonalizedUpsellDrawers() {
    const cards = this.querySelectorAll('[data-ps-personalized-upsell]');
    cards.forEach((card) => this.wirePersonalizedUpsell(card));
  }

  wirePersonalizedUpsell(card) {
    const drawerId = card.dataset.psDrawerId;
    if (!drawerId) return;
    const drawer = document.getElementById(drawerId);
    if (!drawer) return;

    // Reparent to <body> so `position: fixed` isn't trapped by a transformed
    // or overflow-clipped ancestor. Carry the `personalization-steps-block`
    // class so scoped descendant styles (.ps-name-row, .ps-pick-opt, etc.)
    // still resolve after the reparent.
    if (drawer.parentElement !== document.body) {
      drawer.classList.add('personalization-steps-block');
      this.inheritBlockCustomProperties(drawer);
      document.body.appendChild(drawer);
    }

    const checkbox = card.querySelector('.ps-upsell-checkbox');

    // Card click — state machine:
    //   OFF + dialog closed → open dialog (don't toggle yet)
    //   ON  + dialog closed → remove upsell, toggle OFF (values retained)
    //   dialog open         → toggle closes dialog without saving
    card.addEventListener('click', (e) => {
      const moreBtn = e.target.closest('[data-ps-upsell-more]');
      if (moreBtn) return;
      e.preventDefault();

      if (drawer.classList.contains('is-open')) {
        this.closePersonalizedDrawer(drawer);
        return;
      }
      if (checkbox && checkbox.checked) {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        this.openPersonalizedDrawer(drawer);
      }
    });

    // Continue — validate + mark selected + close.
    drawer.querySelectorAll('[data-ps-drawer-confirm]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const containerId = card.dataset.psCardFormId;
        const container = containerId ? document.getElementById(containerId) : drawer;
        const invalid = container
          ? [...container.querySelectorAll('input, select, textarea')].find(
              (el) => !el.disabled && typeof el.checkValidity === 'function' && !el.checkValidity(),
            )
          : null;
        if (invalid) {
          invalid.reportValidity();
          return;
        }
        if (checkbox) {
          checkbox.checked = true;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.closePersonalizedDrawer(drawer);
      });
    });

    // Cancel / X / backdrop — close without saving. Values retained.
    drawer.querySelectorAll('[data-ps-drawer-cancel]').forEach((el) => {
      el.addEventListener('click', () => this.closePersonalizedDrawer(drawer));
    });

    // Escape key while dialog is open.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        this.closePersonalizedDrawer(drawer);
      }
    });

    this.wirePersonalizedVariantRadios(card, drawer);
  }

  openPersonalizedDrawer(drawer) {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      drawer.querySelectorAll('[data-ps-gallery]').forEach((gallery) => {
        if (typeof gallery['_psBindSwiper'] === 'function') gallery['_psBindSwiper']();
      });
      this.refreshDrawerSwipers(drawer);
    });
  }

  closePersonalizedDrawer(drawer) {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
  }

  refreshDrawerSwipers(drawer) {
    drawer.querySelectorAll('swiper-component').forEach((component) => {
      const swipers = [
        component.initializeSwiper,
        component.querySelector('.swiper-main')?.swiper,
        component.querySelector('.swiper-controls__thumbnails')?.swiper,
      ].filter(Boolean);

      swipers.forEach((swiper) => {
        if (typeof swiper.update === 'function') swiper.update();
        if (typeof swiper.updateSlides === 'function') swiper.updateSlides();
        if (typeof swiper.updateProgress === 'function') swiper.updateProgress();
        if (typeof swiper.updateSlidesClasses === 'function') swiper.updateSlidesClasses();
      });

      const mainSwiper = component.querySelector('.swiper-main')?.swiper || component.initializeSwiper;
      if (mainSwiper && typeof mainSwiper.slideTo === 'function') {
        mainSwiper.slideTo(mainSwiper.activeIndex || 0, 0);
      }
    });

    drawer.querySelectorAll('[data-ps-gallery]').forEach((gallery) => {
      if (typeof gallery['_psBindSwiper'] === 'function') gallery['_psBindSwiper']();
    });
  }

  wirePersonalizedVariantRadios(card, scope) {
    const variantContainer = scope.querySelector('[data-ps-card-variants]');
    if (!variantContainer) return;
    const mapScript = variantContainer.querySelector('[data-ps-card-variant-map]');
    if (!mapScript) return;

    let variantMap = {};
    try {
      variantMap = JSON.parse(mapScript.textContent || '{}');
    } catch (e) {
      /* silent */
    }

    const hiddenIdInput = scope.querySelector('[data-ps-card-variant-id-input]');
    const priceEl = card.querySelector('.ps-upsell-pr');
    const drawerPriceCurrent = scope.querySelector('[data-ps-card-price-current]');
    const drawerPriceCompare = scope.querySelector('[data-ps-card-price-compare]');

    const readSelectedOptions = () => {
      const selected = {};
      variantContainer.querySelectorAll('input[type="radio"][data-ps-card-option-index]').forEach((r) => {
        if (r.checked) selected[r.dataset.psCardOptionIndex] = r.value;
      });
      // Rebuild the key in option order
      const keys = Object.keys(selected)
        .map((k) => parseInt(k, 10))
        .sort((a, b) => a - b);
      return keys.map((k) => selected[k]).join('|');
    };

    const applySelectedVariant = () => {
      const key = readSelectedOptions();
      const variant = variantMap[key];
      if (!variant) return;
      card.dataset.upsellVariantId = String(variant.id);
      card.dataset.upsellPrice = String(variant.price);
      if (priceEl) priceEl.textContent = '+' + this.formatMoney(variant.price);
      if (hiddenIdInput) {
        hiddenIdInput.value = String(variant.id);
        hiddenIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (drawerPriceCurrent) {
        drawerPriceCurrent.textContent = this.formatMoney(variant.price);
      }
      if (drawerPriceCompare) {
        const hasCompare = variant.compare_at_price != null && variant.compare_at_price > variant.price;
        if (hasCompare) {
          drawerPriceCompare.textContent = this.formatMoney(variant.compare_at_price);
          drawerPriceCompare.hidden = false;
        } else {
          drawerPriceCompare.hidden = true;
        }
      }
      document.dispatchEvent(
        new CustomEvent('variant:changed', {
          detail: { variant: { id: variant.id }, productFormId: hiddenIdInput?.closest('[id]')?.id },
        }),
      );
      // If this card's checkbox is toggled on, re-total.
      const checkbox = card.querySelector('.ps-upsell-checkbox');
      if (checkbox && checkbox.checked) this.recalcTotals();
    };

    variantContainer.querySelectorAll('input[type="radio"][data-ps-card-option-index]').forEach((r) => {
      r.addEventListener('change', applySelectedVariant);
    });

    // Initial sync so the map is honoured from the start.
    applySelectedVariant();
  }

  setupFetchInterception() {
    if (window._psFetchIntercepted) return;
    window._psFetchIntercepted = true;

    const component = this;
    const originalFetch = window.fetch;

    window.fetch = function (url, config) {
      if (typeof url === 'string' && url.includes('/cart/add') && config?.body instanceof FormData) {
        const upsells = component.getSelectedUpsells();
        if (upsells.length > 0) {
          const formData = config.body;
          const mainProductId = formData.get('id');
          const quantity = formData.get('quantity') || 1;

          const mainItem = {
            id: parseInt(mainProductId),
            quantity: parseInt(quantity),
          };

          const properties = {};
          formData.forEach((value, key) => {
            if (key.startsWith('properties[') && value) {
              const match = key.match(/properties\[(.*?)\]/);
              if (match?.[1]) properties[match[1]] = value;
            }
          });
          if (Object.keys(properties).length > 0) mainItem.properties = properties;

          const items = [mainItem];
          upsells.forEach((upsell) => {
            const item = {
              id: parseInt(upsell.variantId),
              quantity: parseInt(quantity),
            };
            let itemProps = null;
            if (upsell.nested) {
              item.parent_id = parseInt(mainProductId);
              itemProps = {
                _for_variant_id: mainProductId,
                _channel: 'product_page',
                _upsell: 'true',
              };
            }
            if (upsell.properties) {
              itemProps = { ...(itemProps || {}), ...upsell.properties };
            }
            if (itemProps) item.properties = itemProps;
            items.push(item);
          });

          const sections = formData.get('sections');
          const sectionsUrl = formData.get('sections_url');
          const requestBody = { items };
          if (sections) requestBody.sections = sections;
          if (sectionsUrl) requestBody.sections_url = sectionsUrl;

          return originalFetch(url, {
            ...config,
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(requestBody),
          });
        }
      }

      return originalFetch.apply(window, arguments);
    };
  }
}

if (!customElements.get('personalization-steps')) {
  customElements.define('personalization-steps', PersonalizationSteps);
}
